import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';

const { FiX, FiClock, FiBriefcase, FiUser, FiCalendar, FiCheckSquare } = FiIcons;

const TimeEntryModal = ({ isOpen, onClose, entry }) => {
  const { addTimeEntry, updateTimeEntry, projects, team, tasks } = useData();
  const [formData, setFormData] = useState({
    description: '',
    projectId: '',
    taskId: '',
    teamMemberId: '',
    date: new Date().toISOString().split('T')[0],
    hours: 1,
    minutes: 0,
    isBillable: true
  });

  // Filter tasks based on selected project
  const filteredTasks = tasks.filter(t => {
    if (!formData.projectId) return false;
    const proj = projects.find(p => p.id === formData.projectId);
    return t.project === proj?.name;
  });

  useEffect(() => {
    if (entry) {
      const h = Math.floor(entry.duration / 3600);
      const m = Math.floor((entry.duration % 3600) / 60);
      setFormData({
        ...entry,
        hours: h,
        minutes: m
      });
    } else {
      setFormData({
        description: '',
        projectId: '',
        taskId: '',
        teamMemberId: '',
        date: new Date().toISOString().split('T')[0],
        hours: 1,
        minutes: 0,
        isBillable: true
      });
    }
  }, [entry, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const duration = (parseInt(formData.hours) * 3600) + (parseInt(formData.minutes) * 60);
    const payload = { ...formData, duration };
    
    if (entry) await updateTimeEntry(entry.id, payload);
    else await addTimeEntry(payload);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-xl overflow-hidden">
          <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter flex items-center gap-3">
              <SafeIcon icon={FiClock} className="text-blue-600" />
              {entry ? 'Edit Log' : 'Manual Time Log'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="text-slate-400" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div className="space-y-4">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Work Description</label>
              <textarea 
                required
                className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold"
                rows={3}
                placeholder="What did you work on?"
                value={formData.description}
                onChange={e => setFormData({...formData, description: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Project Link</label>
                <div className="relative">
                  <SafeIcon icon={FiBriefcase} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
                  <select 
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold appearance-none"
                    value={formData.projectId}
                    onChange={e => setFormData({...formData, projectId: e.target.value, taskId: ''})}
                  >
                    <option value="">No Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Related Task</label>
                <div className="relative">
                  <SafeIcon icon={FiCheckSquare} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
                  <select 
                    disabled={!formData.projectId}
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold appearance-none disabled:opacity-50"
                    value={formData.taskId}
                    onChange={e => setFormData({...formData, taskId: e.target.value})}
                  >
                    <option value="">No Task</option>
                    {filteredTasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Team Member</label>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" />
                  <select 
                    required
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold appearance-none"
                    value={formData.teamMemberId}
                    onChange={e => setFormData({...formData, teamMemberId: e.target.value})}
                  >
                    <option value="">Select Member</option>
                    {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Date</label>
                <input 
                  type="date"
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold"
                  value={formData.date}
                  onChange={e => setFormData({...formData, date: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Hours</label>
                <input 
                  type="number"
                  min="0"
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold"
                  value={formData.hours}
                  onChange={e => setFormData({...formData, hours: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Minutes</label>
                <input 
                  type="number"
                  min="0"
                  max="59"
                  className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold"
                  value={formData.minutes}
                  onChange={e => setFormData({...formData, minutes: e.target.value})}
                />
              </div>
            </div>

            <button type="submit" className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black shadow-xl hover:bg-blue-700 transition-all">
              {entry ? 'Update Time Log' : 'Save Time Log'}
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TimeEntryModal;